import { router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { useEffect, useState } from "react"

import { type Channel, channelIcons } from "@/components/conversation-channel"
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { homePath, searchResultsPath } from "@/routes"

// IMP-522: nav search over conversations. Results are the feed's rows;
// selecting one lands on its drawer deep link. The copy names what search
// actually covers — names, numbers, and summaries — and stays silent about
// transcripts until they're matchable.

interface SearchRow {
  id: string
  channel: Channel
  person: string | null
  summary: string | null
  day: string
}

export function ClientSearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchRow[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // State resets on close via handleOpenChange rather than an effect, so the
  // effect only synchronizes with the external system (the fetch).
  useEffect(() => {
    if (!open) return

    const timeoutId = setTimeout(() => {
      if (query.trim().length === 0) {
        setResults([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      fetch(`${searchResultsPath()}?q=${encodeURIComponent(query)}`)
        .then((response) => response.json())
        .then((data: { results: SearchRow[] }) => {
          setResults(data.results ?? [])
          setIsLoading(false)
        })
        .catch(() => {
          setResults([])
          setIsLoading(false)
        })
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query, open])

  function handleOpenChange(next: boolean) {
    onOpenChange(next)
    if (!next) {
      setQuery("")
      setResults([])
      setIsLoading(false)
    }
  }

  function handleSelect(row: SearchRow) {
    handleOpenChange(false)
    router.visit(homePath({ conversation: row.id }))
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="h-[350px] overflow-hidden p-0"
        showCloseButton={false}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement
          if (target.closest("[cmdk-item]")) {
            e.preventDefault()
          }
        }}
      >
        {/* Matching happens server-side; cmdk must not re-filter. */}
        <Command
          shouldFilter={false}
          className="bg-background rounded-lg border-none"
        >
          <CommandInput
            placeholder="Search conversations"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="bg-background">
            {query.trim().length === 0 ? (
              <div className="text-muted-foreground flex h-[290px] flex-col items-center justify-center gap-2 text-sm">
                <Search className="size-5" />
                <p>Search your conversations</p>
                <p className="text-xs">Names, numbers, and summaries</p>
              </div>
            ) : results.length === 0 && !isLoading ? (
              <div className="text-muted-foreground py-6 text-center text-sm">
                No conversations matched.
              </div>
            ) : (
              results.map((row) => {
                const Icon = channelIcons[row.channel]
                return (
                  <CommandItem
                    key={row.id}
                    value={row.id}
                    onSelect={() => handleSelect(row)}
                    className="pointer-events-auto cursor-pointer px-4 py-3"
                    style={{ pointerEvents: "auto" }}
                  >
                    <div className="flex w-full items-start gap-3">
                      <Icon className="mt-0.5 size-3.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="truncate text-sm font-semibold">
                            {row.person ?? "Anonymous visitor"}
                          </span>
                          <span className="text-muted-foreground ml-auto shrink-0 text-xs">
                            {row.day}
                          </span>
                        </div>
                        {row.summary && (
                          <p className="text-muted-foreground mt-0.5 line-clamp-2 text-sm">
                            {row.summary}
                          </p>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                )
              })
            )}
            {isLoading && (
              <div className="text-muted-foreground py-2 text-center text-xs">
                Searching...
              </div>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
